import { apiAuthCaller } from '@services/apiCaller';
import { ISourceDefinition } from '@stores/sourceDefinitionsList';
import { action, computed, observable } from 'mobx';

import { IRespData } from '@components/sources/liveEvents';
import { getApiErrorMessage } from '@components/common/util/util';
import {
  Catalog,
  CatalogAsConfig,
} from '@components/sources/source/singerSource/singerResourceSelection';
import { IResourceConfig } from '@components/sources/source/cloudSource/cloudSourceResources';
import { Resource } from '@components/sources/source/cloudSource/cloudSourceSettings';
import { JSONSchema } from '@components/common/jsonSchemaForm/jsonSchemaForm';
import { ColumnSelection } from '@components/sources/source/cloudSource/resourceSelection/columnSelection/ColumnSelectionView';
import {
  EventStatParams,
  TrackEventParams,
} from '@components/sources/source-view/source-events/hooks';
import { BATCH_SIZE } from '@components/sources/source-view/source-events/utils';
import { ScheduleI } from '@components/sources/source/cloudSource/cloudSourceSchedule/schedules';
import { IAccount } from '@components/sources/source/warehouseSource/auth/authAccounts';
import { IDestinationStore } from './destination';
import { IRootStore } from './index';

export interface AccountInfo extends Partial<IAccount> {
  skipValidation?: boolean;
}

interface WarehouseSourceConfig {
  isCredentialsValidationSupported?: boolean;
}
export interface SourceConfig extends WarehouseSourceConfig {
  constants?: {
    key: string;
    value: string;
  }[];
  query?: {
    table: {
      schema: string;
      name: string;
    };
    columns: string[];
  };
  mappings?: { original: string; mapped: string }[];
  prefix?: string;
  bucketName?: string;
  map_important_overrides?: Record<string, string>;
  syncBehaviour?: {
    mode: string;
  };
  schedule?: ScheduleI;
  accountId?: string;
  rudderAccountId?: string;
  catalogJSONSchema?: Catalog[];
  catalog?: {
    streams: CatalogAsConfig[];
  };
  config?: Record<string, string>;
  configJSONSchema?: JSONSchema;
  accountInfo?: AccountInfo;
  customResources?: IResourceConfig[] | null;
  credentials?: unknown;
  accessToken?: string;
  refreshToken?: string;
  role?: string;
  resources?: Resource[];
  resourcesColumns?: Record<string, ColumnSelection>;
  isSampleSource?: boolean;
}

export type SecretConfig = Record<string, unknown>;

interface EventStats {
  graph: {
    bucket: number;
    sentCount: number;
  }[];
  totalSent: number;
}

export interface TrackEvent {
  name: string;
  count: number;
  lastSent: number;
}

export interface TPSettings {
  dropUnplannedEvents: boolean;
  dropUnplannedProperties: boolean;
  dropOtherViolation: boolean;
  propagateValidationErrors: boolean;
  sendViolatedEventsTo: string;
}

export interface ITPConfig {
  track: TPSettings;
}

export interface ISourceTPConfig {
  config: ITPConfig;
  trackingPlanId: string;
}

export interface ISource {
  id: string;
  name: string;
  writeKey: string;
  createdAt: string;
  enabled: boolean;
  config: SourceConfig;
  secretConfig: SecretConfig;
  sourceDefinitionId: string;
  sourceDef: ISourceDefinition;
  destinations: IDestinationStore[];
  category?: string;
  eventStats?: EventStats;
  transient: boolean;
  trackEvents?: {
    data?: TrackEvent[];
    hasMore: boolean;
  };
  trackingPlanConfig: ISourceTPConfig | undefined;
}
export interface ISourceStore extends ISource {
  rootStore: IRootStore;
  setName(name: string): void;
  updateConfig(config?: SourceConfig, hardUpdate?: boolean): Promise<boolean>;
  getLiveEvents?(latestId: number, from: number): Promise<IRespData[]>;
  getEventStats?: (params: EventStatParams, callback: () => void) => void;
  getTrackEvents?: (params: TrackEventParams, callback?: () => void) => void;
}

export type SourceConstructor = {
  id: string;
  name: string;
  createdAt: string;
  writeKey: string;
  enabled: boolean;
  config: SourceConfig;
  secretConfig: SecretConfig;
  sourceDefinitionId: string;
  transient: boolean;
  trackingPlanConfig: ISourceTPConfig | undefined;
};

export class SourceStore implements ISourceStore {
  @observable public id: string;

  @observable public name: string;

  @observable public createdAt: string;

  @observable public writeKey: string;

  @observable public enabled: boolean;

  @observable public config: SourceConfig;

  @observable public transient: boolean;

  @observable public secretConfig: SecretConfig;

  @observable public sourceDefinitionId: string;

  @observable public trackingPlanConfig: ISourceTPConfig | undefined;

  @observable public rootStore: IRootStore;

  @observable eventStats: EventStats | undefined;

  @observable trackEvents: { data?: TrackEvent[]; hasMore: boolean } | undefined;

  constructor(source: SourceConstructor, rootStore: IRootStore) {
    this.id = source.id;
    this.name = source.name;
    this.createdAt = source.createdAt;
    this.writeKey = source.writeKey;
    this.enabled = source.enabled;
    this.config = source.config;
    this.secretConfig = source.secretConfig;
    this.sourceDefinitionId = source.sourceDefinitionId;
    this.transient = source.transient;
    const prevState = rootStore.sourcesListStore.sourceById(this.id);
    this.eventStats = prevState?.eventStats;
    this.trackEvents = prevState?.trackEvents;
    this.trackingPlanConfig = source.trackingPlanConfig;
    this.rootStore = rootStore;
  }

  @action.bound
  public setName(name: string): void {
    this.name = name;
  }

  @computed get sourceDef() {
    return this.rootStore.sourceDefinitionsListStore.getSourceDef(this.sourceDefinitionId)!;
  }

  @computed get destinations() {
    const destIds = this.rootStore.connectionsStore.connections[this.id] || [];
    return this.rootStore.destinationsListStore.destinations.filter((dest) =>
      destIds.includes(dest.id),
    );
  }

  @computed get category() {
    return this.sourceDef.category;
  }

  @action.bound
  public async updateConfig(config: SourceConfig, hardUpdate = false) {
    const {
      userStore: { token },
      messagesStore,
    } = this.rootStore;
    try {
      const updateUrl = `/sources/${this.id}${hardUpdate ? '?hardUpdate=true' : ''}`;
      const res = await apiAuthCaller(token).post(updateUrl, {
        config,
      });
      this.config = res.data.config;
      return (await res.status) === 200;
    } catch (error) {
      messagesStore.showErrorMessage(
        getApiErrorMessage(error, 'Failed to update source configuration.'),
      );
      return false;
    }
  }

  public async getLiveEvents(latestId: number, timestamp: number) {
    const {
      userStore: { token },
    } = this.rootStore;
    try {
      const resp = await apiAuthCaller(token).get(
        `/eventUploads?id=${latestId}&from=${timestamp}`,
        {
          headers: { 'X-WRITE-KEY': this.writeKey },
        },
      );
      return resp.data.map((e: { event: unknown }) => ({
        ...e,
        payload: e.event,
      }));
    } catch (err) {
      return [];
    }
  }

  getEventStats(params: EventStatParams, callback: () => void) {
    const { start, aggregationMinutes } = params;
    const {
      userStore: { token },
      messagesStore,
    } = this.rootStore;
    apiAuthCaller(token)
      .get(`/sources/${this.id}/eventMetrics`, {
        params: {
          start,
          aggregationMinutes,
        },
      })
      .then((res) => {
        const { aggregates, totalSent } = res.data;
        this.eventStats = {
          graph: aggregates,
          totalSent,
        };
      })
      .catch((err) => {
        messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to get source events data'));
      })
      .finally(() => {
        callback();
      });
  }

  getTrackEvents(params: TrackEventParams, callback?: () => void) {
    const isFirstCall = params.page === 0;
    if (isFirstCall) {
      this.trackEvents = undefined;
    }
    const {
      userStore: { token },
      messagesStore,
    } = this.rootStore;
    apiAuthCaller(token)
      .get(`/sources/${this.id}/events`, { params })
      .then((res) => {
        this.trackEvents = {
          ...this.trackEvents,
          hasMore: res.data.length === BATCH_SIZE,
        };
        if (res.data.length === 0 && !isFirstCall) {
          messagesStore.showErrorMessage('No more events found');
        } else {
          this.trackEvents.data = isFirstCall ? res.data : this.trackEvents.data?.concat(res.data);
        }
      })
      .catch((err) => {
        messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to get event names list'));
      })
      .finally(() => {
        callback?.();
      });
  }
}
